<script setup lang="ts">
import { computed, inject } from "vue";
import type { TagGroupProps } from "~/components/clarity-ui/Tag/TagGroup";

export interface TagProps {
  /** The color of the tag. Defines its visual meaning. */
  color?:
    | "information"
    | "success"
    | "failure"
    | "cancel"
    | "new"
    | "deal"
    | "highlight"
    | "light"
    | "dark";
  /** Show an outlined version of the tag. */
  outlined?: boolean;
  /** Size of the tag. */
  size?: "small" | "medium";
}

// A boolean defaults always to false in Vue. To be able to use the parent's value, it needs to default
// to undefined.
// outlined is not set -> parent's value for outlined or parent's default value for outlined will be used
// outlined is set -> child's value for outlined will be used regardless of parent's value for outlined
const { color, outlined = undefined, size } = defineProps<TagProps>();

const tagGroupProps = inject<TagGroupProps>("tagGroupContext", {});

const computedColor = computed(() => color || tagGroupProps?.color || "information");
const computedOutlined = computed(() => outlined ?? tagGroupProps?.outlined ?? false);
const computedSize = computed(() => size || tagGroupProps?.size || "medium");
</script>

<template>
  <span
    class="inline-block rounded-sm border border-transparent px-1.5 py-1 text-sm leading-none"
    :class="[
      `tag-${computedColor}`,
      {
        'tag-outlined': computedOutlined,
        [`tag-${computedSize}`]: computedSize !== 'medium',
      },
    ]"
    role="status"
    data-testid="clr-tag"
    ><slot
  /></span>
</template>

<style lang="postcss" scoped>
.tag-information {
  @apply bg-highlight-03 text-base-03;

  &.tag-outlined {
    @apply border-highlight-02 text-highlight-02;
  }
}

.tag-success {
  @apply bg-success-01 text-base-02;

  &.tag-outlined {
    @apply border-information-01 text-information-01;
  }
}

.tag-failure {
  @apply bg-error-01 text-base-02;

  &.tag-outlined {
    @apply border-error-01 text-error-01;
  }
}

.tag-cancel {
  @apply bg-disabled-02 text-base-02;

  &.tag-outlined {
    @apply border-disabled-01 text-disabled-01;
  }
}

.tag-new {
  @apply bg-highlight-06 text-base-02;

  /* missing
  &.tag-outlined {
    @apply border-light-medium-blue;
  }  */
}

.tag-deal {
  @apply bg-highlight-04 text-base-02;

  /* missing
  &.tag-outlined {
    @apply border-light-pink;
  }  */
}

.tag-highlight {
  @apply bg-highlight-08 text-base-02;

  /* missing
  &.tag-outlined {
    @apply border-highlight-green;
  }  */
}

.tag-light {
  @apply bg-white text-base-02;

  &.tag-outlined {
    @apply border-white text-white backdrop-blur-lg;
  }
}

.tag-dark {
  @apply bg-black text-white;

  &.tag-outlined {
    @apply border-black text-black backdrop-blur-lg;
  }
}

.tag-outlined {
  @apply bg-transparent;
}

.tag-small {
  @apply text-xs leading-none;
}
</style>
